import { ComponentType, createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { CustomDevice } from '@/core/models/custom-device.model'

import ChimeProvider from '@/providers/ChimeProvider'
import PeerjsProvider from '@/providers/PeerjsProvider'
import RpiProvider from '@/providers/rpi/RpiProvider'

import Chime from './services/Chime'
import Peerjs from './services/Peerjs'
import Rpi from './services/Rpi'
import Offline from './services/Offline'
import { DiagnosticRTCDevices, DiagnosticRTCServiceHandle } from './types'

export enum DiagnosticRTCServiceType {
  Peerjs = 'peerjs',
  Chime = 'chime',
  Rpi = 'rpi',
  Offline = 'offline',
}

export type DiagnosticRTCProviderContextType = Omit<DiagnosticRTCServiceHandle, 'getStream'> & {
  activeDiagnosticRTCService?: DiagnosticRTCServiceType
  changeDiagnosticRTCService: (service: DiagnosticRTCServiceType) => void
  devices: DiagnosticRTCDevices
  diagnosticRTCServices: DiagnosticRTCServiceType[]
  isStreamer: boolean
  selectedDeviceId: string | null
  stream: MediaStream | null
}

export type DiagnosticRTCProviderProps = PropsWithChildren<{
  config: {
    [DiagnosticRTCServiceType.Chime]?: {
      ecgSourceUserId?: string
      attendeeInfo: Record<string, any> | null
      meetingInfo: Record<string, any> | null
    }
    [DiagnosticRTCServiceType.Peerjs]?: {
      config?: RTCConfiguration
      peerId: string | null
      streamer: string
    }
    [DiagnosticRTCServiceType.Rpi]?: {
      meetingID: string
      serverUrl: string
    }
    [DiagnosticRTCServiceType.Offline]?: {
      // Offline service doesn't need additional config
    }
  }
  customDevices: Array<CustomDevice & { available?: boolean }>
  isStreamer?: boolean
  onRecordingStop?: (recordingFile: File) => void
  type: 'audio' | 'video'
}>

export const DiagnosticRTCProviderContext = createContext<DiagnosticRTCProviderContextType>({
  activeDiagnosticRTCService: DiagnosticRTCServiceType.Peerjs,
  captureScreen: () => { },
  changeDiagnosticRTCService: () => { },
  devices: {
    medicalCameras: [],
    stethoscopes: [],
    audioInputMediaDevices: [],
    videoMediaDevices: [],
  },
  diagnosticRTCServices: [],
  isStreamer: false,
  renderAudio: () => <></>,
  renderAudioVisualizer: () => <></>,
  renderVideo: () => <></>,
  selectDevice: () => { },
  selectedDeviceId: null,
  shareScreen: () => { },
  stopMicro: () => { },
  stopVideo: () => { },
  stream: null,
})

const initialDevices = { medicalCameras: [], stethoscopes: [], audioInputMediaDevices: [], videoMediaDevices: [] }

export function DiagnosticRTCProvider(props: DiagnosticRTCProviderProps) {
  const { children, config, customDevices, isStreamer = false, onRecordingStop, type } = props
  const rtcServiceRef = useRef<DiagnosticRTCServiceHandle | null>(null)

  const [activeDiagnosticRTCService, setActiveDiagnosticRTCService] = useState<DiagnosticRTCServiceType>()
  const [devices, setDevices] = useState<DiagnosticRTCDevices>(initialDevices)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)

  const captureScreen = useCallback((cb: (imageFile: File) => void) => {
    rtcServiceRef.current?.captureScreen(cb)
  }, [])

  const changeDiagnosticRTCService = useCallback((service: DiagnosticRTCServiceType) => {
    setDevices(initialDevices)
    setSelectedDeviceId(null) // reset selected device
    setStream(null) // reset stream
    rtcServiceRef.current = null
    setActiveDiagnosticRTCService(service)
  }, [])

  const refreshConnection = useCallback(() => {
    rtcServiceRef.current?.refreshConnection?.()
  }, [])

  const renderAudio = useCallback(() => {
    return rtcServiceRef.current?.renderAudio() || <></>
  }, [])

  const renderAudioVisualizer = useCallback(() => {
    return rtcServiceRef.current?.renderAudioVisualizer() || <></>
  }, [])

  const renderVideo = useCallback(() => {
    return rtcServiceRef.current?.renderVideo() || <></>
  }, [])

  const selectDevice = useCallback((deviceId: string) => {
    rtcServiceRef.current?.selectDevice(deviceId)
    setSelectedDeviceId(deviceId)
  }, [])

  const selectResolution = useCallback((deviceId: string, resolution: string) => {
    rtcServiceRef.current?.selectResolution?.(deviceId, resolution)
  }, [])

  const shareScreen = useCallback((isScreenSharing: boolean) => {
    rtcServiceRef.current?.shareScreen(isScreenSharing)
  }, [])

  const stopMicro = useCallback(() => {
    rtcServiceRef.current?.stopMicro()
    setSelectedDeviceId(null)
  }, [])

  const stopVideo = useCallback(() => {
    rtcServiceRef.current?.stopVideo()
    setSelectedDeviceId(null)
  }, [])

  const handleDevicesChange = useCallback((devices: DiagnosticRTCDevices) => {
    setDevices(devices)
  }, [])

  const handleStreamChange = useCallback((stream: MediaStream | null) => {
    setStream(stream)
  }, [])

  const renderRTCService = useCallback(
    (service: DiagnosticRTCServiceType) => {
      const serviceProps = {
        customDevices,
        onDevicesChange: handleDevicesChange,
        onRecordingStop,
        onStreamChange: handleStreamChange,
        type,
      }
      switch (service) {
        case DiagnosticRTCServiceType.Chime:
          const chimeConfigProps = config[DiagnosticRTCServiceType.Chime]
          return chimeConfigProps ? (
            <Chime {...chimeConfigProps} ref={rtcServiceRef} {...serviceProps}>
              {children}
            </Chime>
          ) : null
        case DiagnosticRTCServiceType.Peerjs:
          const peerjsService = config[service]
          return !!peerjsService ? (
            <Peerjs {...peerjsService} ref={rtcServiceRef} {...serviceProps}>
              {children}
            </Peerjs>
          ) : null
        case DiagnosticRTCServiceType.Rpi:
          return (
            <Rpi {...serviceProps} ref={rtcServiceRef}>
              {children}
            </Rpi>
          )
        case DiagnosticRTCServiceType.Offline:
          return (
            <Offline {...serviceProps} ref={rtcServiceRef}>
              {children}
            </Offline>
          )
        default:
          return children
      }
    },
    [children, config, customDevices, handleDevicesChange, handleStreamChange, onRecordingStop, type]
  )

  const diagnosticRTCServices = useMemo(
    () => Object.keys(config).filter((key) => config[key as keyof typeof config] !== undefined) as DiagnosticRTCServiceType[],
    [config]
  )

  const providers = useMemo(() => {
    const peerjsConfigProps = config[DiagnosticRTCServiceType.Peerjs]
    const chimeConfigProps = config[DiagnosticRTCServiceType.Chime]
    const rpiConfigProps = config[DiagnosticRTCServiceType.Rpi]
    const offlineConfigProps = config[DiagnosticRTCServiceType.Offline]

    const result: Array<{ provider: ComponentType<any>; props?: Record<string, any>; serviceType: DiagnosticRTCServiceType }> = []

    if (!!peerjsConfigProps)
      result.push({
        provider: PeerjsProvider,
        props: { config: peerjsConfigProps.config, isStreamer, peerId: peerjsConfigProps.peerId },
        serviceType: DiagnosticRTCServiceType.Peerjs,
      })

    if (!!chimeConfigProps) result.push({ provider: ChimeProvider, serviceType: DiagnosticRTCServiceType.Chime })

    if (!!rpiConfigProps) {
      const { meetingID, serverUrl } = rpiConfigProps
      result.push({ provider: RpiProvider, props: { isStreamer, meetingID, serverUrl }, serviceType: DiagnosticRTCServiceType.Rpi })
    }

    // Offline service doesn't need external providers, just add a placeholder
    if (!!offlineConfigProps) {
      result.push({
        provider: ({ children }: { children: React.ReactNode }) => <>{children}</>,
        serviceType: DiagnosticRTCServiceType.Offline
      })
    }

    return result
  }, [config, isStreamer])

  useEffect(() => {
    if (!activeDiagnosticRTCService && !!providers.length) {
      setActiveDiagnosticRTCService(providers[0].serviceType)
    }
  }, [activeDiagnosticRTCService, providers])

  const contextValue = useMemo(
    () => ({
      activeDiagnosticRTCService,
      captureScreen,
      changeDiagnosticRTCService,
      devices,
      diagnosticRTCServices,
      isStreamer,
      refreshConnection,
      renderAudio,
      renderAudioVisualizer,
      renderVideo,
      selectDevice,
      selectResolution,
      selectedDeviceId,
      shareScreen,
      stopMicro,
      stopVideo,
      stream,
    }),
    [
      activeDiagnosticRTCService,
      captureScreen,
      changeDiagnosticRTCService,
      devices,
      diagnosticRTCServices,
      isStreamer,
      refreshConnection,
      renderAudio,
      renderAudioVisualizer,
      renderVideo,
      selectDevice,
      selectResolution,
      selectedDeviceId,
      shareScreen,
      stopMicro,
      stopVideo,
      stream,
    ]
  )

  return (
    <DiagnosticRTCProviderContext.Provider value={contextValue}>
      {!!activeDiagnosticRTCService &&
        providers.reduceRight((acc, prov) => {
          let Provider = prov.provider
          if (!!prov.props) {
            return <Provider {...prov.props}>{acc}</Provider>
          }

          return <Provider>{acc}</Provider>
        }, renderRTCService(activeDiagnosticRTCService))}
    </DiagnosticRTCProviderContext.Provider>
  )
}

export function useDiagnosticRTC(): DiagnosticRTCProviderContextType {
  const context = useContext(DiagnosticRTCProviderContext)

  if (!context) {
    throw new Error('useDiagnosticRTC must be used within a DiagnosticRTCProvider')
  }

  return context
}
